/**
 * Patient Detail Page
 */
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  Description as DocumentIcon,
  AccountCircle as AccountIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContactPhone as CrmIcon,
} from '@mui/icons-material';
import { PageHeader, ConfirmDialog } from '../../components/common';
import {
  useGetPatientQuery,
  useDeletePatientMutation,
} from '../../store/api/patientsApi';
import { useState } from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';
import { usePermissions } from '../../hooks/usePermissions';
import PatientMedicalFileSection from '../../components/patients/PatientMedicalFileSection';
import PatientOrdersSection from '../../components/patients/PatientOrdersSection';
import { useTranslation } from 'react-i18next';

const PatientDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManagePatients = hasPermission('manage_patients');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data, isLoading, error } = useGetPatientQuery(Number(id));
  const [deletePatient, { isLoading: isDeleting }] = useDeletePatientMutation();

  const patient = data?.data;

  const handleDelete = async () => {
    if (!id) return;
    try {
      await deletePatient(Number(id)).unwrap();
      toast.success(t('users.toast.deleteSuccess'));
      navigate('/users');
    } catch (err: any) {
      toast.error(err?.data?.message || t('users.toast.deleteError'));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !patient) {
    const err = error as { status?: number; data?: { message?: string } } | undefined;
    const isForbidden = err?.status === 403;
    const message = isForbidden
      ? err?.data?.message || t('users.errors.forbidden')
      : t('users.errors.notFound');

    return (
      <Box sx={{ p: 3, textAlign: 'center', py: 10 }}>
        <Typography color="error">{message}</Typography>
        <Button onClick={() => navigate('/users')} sx={{ mt: 2 }}>
          {t('users.buttons.backToPatients')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={patient.full_name}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('users.breadcrumb'), path: '/users' },
          { label: patient.full_name },
        ]}
        action={
          canManagePatients ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/users/patients/${id}/edit`)}
              >
                {t('common.edit')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                {t('common.delete')}
              </Button>
            </Box>
          ) : undefined
        }
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/users')}
        sx={{ mb: 3 }}
      >
        {t('users.buttons.backToPatients')}
      </Button>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '2fr 1fr',
          },
          gap: 3,
        }}
      >
        {/* Main Content */}
        <Box>
          {/* Basic Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('users.sections.basicInfo')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.fullName')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {patient.full_name}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.dateOfBirth')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {patient.date_of_birth
                      ? moment(patient.date_of_birth).format('MMMM DD, YYYY')
                      : t('users.na')}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.age')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {patient.age != null ? t('users.ageValue', { count: patient.age }) : t('users.na')}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.gender')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {patient.gender === 'M'
                      ? t('users.gender.M')
                      : patient.gender === 'F'
                        ? t('users.gender.F')
                        : t('users.na')}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.nationalId')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {patient.national_id || t('users.na')}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.nationality')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {patient.nationality || t('users.na')}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.status')}
                  </Typography>
                  <Chip
                    label={patient.is_active ? t('users.status.ACTIVE') : t('users.status.INACTIVE')}
                    color={patient.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <AccountIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('users.sections.contactInfo')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.phoneNumber')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {patient.phone_number}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.email')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {patient.email || t('users.na')}
                  </Typography>
                </Grid>

                {patient.user_email && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('users.form.linkedUserAccount')}
                      </Typography>
                      <Chip
                        label={patient.user_full_name || patient.user_email}
                        color="primary"
                        size="small"
                      />
                      {patient.user_email && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          ({patient.user_email})
                        </Typography>
                      )}
                    </Grid>
                  </>
                )}

                {patient.emergency_contact_name && (
                  <>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('users.form.emergencyContactName')}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {patient.emergency_contact_name}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('users.form.emergencyContactPhone')}
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {patient.emergency_contact_phone || t('users.na')}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Lead & contact preferences */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CrmIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('users.sections.crmInfo')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.leadSource')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ whiteSpace: 'pre-line' }}>
                    {patient.lead_source ? t(`users.leadSources.${patient.lead_source}`) : t('users.na')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.leadSourceDetails')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ whiteSpace: 'pre-line' }}>
                    {patient.lead_source_details || t('users.na')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.contactMethod')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ whiteSpace: 'pre-line' }}>
                    {patient.preferred_contact_method
                      ? t(`users.contactMethods.${patient.preferred_contact_method}`)
                      : t('users.na')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.contactTime')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ whiteSpace: 'pre-line' }}>
                    {patient.preferred_contact_time || t('users.na')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.contactNotes')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ whiteSpace: 'pre-line' }}>
                    {patient.contact_notes || t('users.na')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.previousExperience')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500} sx={{ whiteSpace: 'pre-line' }}>
                    {patient.previous_home_care_experience || t('users.na')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Medical Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <HospitalIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight={600}>
                  {t('users.sections.medicalInfo')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.bloodType')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {patient.blood_type || t('users.na')}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.bmi')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {patient.bmi ? patient.bmi.toFixed(2) : t('users.na')}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.height')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {patient.height ? `${patient.height} cm` : t('users.na')}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.weight')}
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {patient.weight ? `${patient.weight} kg` : t('users.na')}
                  </Typography>
                </Grid>

                {patient.health_conditions && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('users.form.healthConditions')}
                      </Typography>
                      <Typography variant="body1">{patient.health_conditions}</Typography>
                    </Grid>
                  </>
                )}

                {patient.allergies && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('users.form.allergies')}
                      </Typography>
                      <Typography variant="body1">{patient.allergies}</Typography>
                    </Grid>
                  </>
                )}

                {patient.current_medications && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('users.form.currentMedications')}
                      </Typography>
                      <Typography variant="body1">{patient.current_medications}</Typography>
                    </Grid>
                  </>
                )}

                {patient.past_surgeries && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('users.form.pastSurgeries')}
                      </Typography>
                      <Typography variant="body1">{patient.past_surgeries}</Typography>
                    </Grid>
                  </>
                )}

                {patient.family_medical_history && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('users.form.familyMedicalHistory')}
                      </Typography>
                      <Typography variant="body1">{patient.family_medical_history}</Typography>
                    </Grid>
                  </>
                )}

                {patient.special_notes && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {t('users.form.specialNotes')}
                      </Typography>
                      <Typography variant="body1">{patient.special_notes}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Insurance Information */}
          {patient.has_insurance && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <HospitalIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight={600}>
                    {t('users.sections.insuranceInfo')}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('users.form.insuranceProvider')}
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {patient.insurance_provider || t('users.na')}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('users.form.policyNumber')}
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {patient.insurance_policy_number || t('users.na')}
                    </Typography>
                  </Grid>

                  {patient.insurance_expiry_date && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                          {t('users.form.expiryDate')}
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {moment(patient.insurance_expiry_date).format('MMMM DD, YYYY')}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Sidebar */}
        <Box>
          {/* Status Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('users.sections.patientStatus')}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={patient.is_active ? t('users.status.ACTIVE') : t('users.status.INACTIVE')}
                  color={patient.is_active ? 'success' : 'default'}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('users.form.hasInsurance')}
                  </Typography>
                  <Chip
                    label={patient.has_insurance ? t('common.yes') : t('common.no')}
                    color={patient.has_insurance ? 'success' : 'default'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('users.sections.medicalDocuments')}
                  </Typography>
                  <Chip
                    label={patient.has_medical_documents ? t('users.documents.available') : t('users.documents.notAvailable')}
                    color={patient.has_medical_documents ? 'info' : 'default'}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Medical Documents */}
          {patient.has_medical_documents && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DocumentIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" fontWeight={600}>
                    {t('users.sections.medicalDocuments')}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {patient.medical_reports && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('users.form.medicalReports')}
                    </Typography>
                    <Link href={patient.medical_reports} target="_blank" rel="noopener">
                      {t('users.documents.viewMedicalReports')}
                    </Link>
                  </Box>
                )}

                {patient.prescriptions && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('users.form.prescriptions')}
                    </Typography>
                    <Link href={patient.prescriptions} target="_blank" rel="noopener">
                      {t('users.documents.viewPrescriptions')}
                    </Link>
                  </Box>
                )}

                {patient.id_document && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      {t('users.form.idDocument')}
                    </Typography>
                    <Link href={patient.id_document} target="_blank" rel="noopener">
                      {t('users.documents.viewIdDocument')}
                    </Link>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Metadata */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {t('users.sections.metadata')}
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  {t('users.form.createdAt')}
                </Typography>
                <Typography variant="body2">
                  {moment(patient.created_at).format('MMMM DD, YYYY HH:mm')}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  {t('users.form.updatedAt')}
                </Typography>
                <Typography variant="body2">
                  {moment(patient.updated_at).format('MMMM DD, YYYY HH:mm')}
                </Typography>
              </Box>

              {patient.created_by_email && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('users.form.createdBy')}
                  </Typography>
                  <Typography variant="body2">{patient.created_by_email}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <PatientOrdersSection patientId={patient.id} />

      <PatientMedicalFileSection patientId={patient.id} canManage={canManagePatients} />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title={t('users.dialog.deleteTitle')}
        message={t('users.dialog.deleteMessagePlain', { name: patient.full_name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        loading={isDeleting}
      />
    </Box>
  );
};

export default PatientDetailPage;

